class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.scrollPosition = 0;
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.cartDrawerBound === 'true') return;

    overlay.dataset.cartDrawerBound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space' || event.code === 'Spacebar') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    this.lockPageScroll();
    document.body.classList.add('overflow-hidden', 'nawiya-cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'nawiya-cart-drawer-open');
    this.unlockPageScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.id) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');
      event.currentTarget.setAttribute('aria-expanded', details?.hasAttribute('open') ? 'false' : 'true');
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const shouldOpen = options.open !== false;

    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHTML) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.updateCartCount(parsedState.item_count);

    document.dispatchEvent(
      new CustomEvent('nawiya:cart-rendered', {
        detail: { cartData: parsedState, opened: shouldOpen },
      })
    );

    if (shouldOpen) this.open();
  }

  updateCartCount(itemCount) {
    const count = Number.parseInt(itemCount, 10) || 0;
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    let badge = cartLink.querySelector('.cart-count-bubble');

    if (count <= 0) {
      badge?.remove();
    } else {
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'cart-count-bubble';
        badge.dataset.cartCount = '';
        badge.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        cartLink.appendChild(badge);
      }

      const visibleCount = badge.querySelector('[aria-hidden="true"]');
      const accessibleCount = badge.querySelector('.visually-hidden');
      if (visibleCount) visibleCount.textContent = count > 99 ? '99+' : String(count);
      if (accessibleCount) accessibleCount.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
      badge.classList.remove('nawiya-cart-count--bump');
      requestAnimationFrame(() => badge.classList.add('nawiya-cart-count--bump'));
    }

    const mobileCount = document.querySelector('[data-nawiya-mobile-cart-count]');
    if (mobileCount) {
      mobileCount.hidden = count <= 0;
      mobileCount.textContent = count > 99 ? '99+' : String(count);
    }
  }

  lockPageScroll() {
    if (document.body.dataset.nawiyaScrollLocked === 'true') return;

    this.scrollPosition = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.dataset.nawiyaScrollLocked = 'true';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockPageScroll() {
    if (document.body.dataset.nawiyaScrollLocked !== 'true') return;

    const storedPosition = this.scrollPosition || Math.abs(Number.parseInt(document.body.style.top, 10)) || 0;
    delete document.body.dataset.nawiyaScrollLocked;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, storedPosition);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    return parsedDocument.querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
